import {
    useCallback, useEffect, useRef, useState, useMemo
} from 'react';
import OutsideClick from '@common/OutsideClick';
import Context from './Context';

const getMenuContainerElement = () => document.body;

const getScrollElement = () => {
    const { scrollingElement, documentElement } = document;

    return scrollingElement || documentElement;
};

export function Dropdown({
    children,
    modalId,
    settings: settingsOverrides = {},
    setActiveModalId = () => null
}) {
    const settings = useMemo(() => ({
        stickHorizontallyTo: '',
        hasMouseMoveEvents: false,
        hasTriggerWidth: false,
        ...settingsOverrides
    }), [settingsOverrides]);
    const containerRef = useRef();
    const [position, setPosition] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef();
    const hideTimeoutRef = useRef();
    const outsideClickListenerRef = useRef();
    const { hasTriggerWidth, stickHorizontallyTo } = settings;

    const handleHide = useCallback(() => {
        setIsOpen(false);
        setActiveModalId(null);
    }, [setActiveModalId]);

    const hideDropdown = useCallback((event, hasToUseDelay) => {
        if (hasToUseDelay) {
            hideTimeoutRef.current = setTimeout(handleHide, 100);
        } else {
            handleHide();
        }
    }, [handleHide]);

    const cancelDelayedHide = () => {
        clearTimeout(hideTimeoutRef.current);
    };

    const showDropdown = useCallback(() => {
        if (!containerRef.current) {
            containerRef.current = document.createElement('div');
        }

        if (!outsideClickListenerRef.current) {
            outsideClickListenerRef.current = new OutsideClick(triggerRef.current, hideDropdown, false);
        }

        setActiveModalId(modalId);
        setIsOpen(true);
        cancelDelayedHide();
    }, [hideDropdown, modalId, setActiveModalId]);

    const adjustPosition = useCallback(() => {
        const scrollElement = getScrollElement();
        const {
            top,
            left,
            right,
            height
        } = triggerRef.current.getBoundingClientRect();
        const { scrollLeft, scrollTop, offsetWidth } = scrollElement;
        const positionStyling = {
            top: `${top + height + scrollTop}px`
        };

        if (stickHorizontallyTo === 'right') {
            positionStyling.right = `${offsetWidth - right}px`;
        } else if (stickHorizontallyTo === 'left') {
            positionStyling.left = `${scrollLeft}px`;
        } else {
            positionStyling.left = `${left + scrollLeft}px`;
        }

        if (hasTriggerWidth) {
            positionStyling.width = `${triggerRef.current.offsetWidth}px`;
        }

        setPosition(positionStyling);
    }, [hasTriggerWidth, stickHorizontallyTo]);

    useEffect(() => {
        const menuContainerElement = getMenuContainerElement();

        if (isOpen) {
            menuContainerElement.appendChild(containerRef.current);
            adjustPosition();
            outsideClickListenerRef.current.register();
        }

        return () => {
            if (outsideClickListenerRef.current) {
                outsideClickListenerRef.current.unregister();
            }

            if (menuContainerElement.contains(containerRef.current)) {
                menuContainerElement.removeChild(containerRef.current);
            }
        };
    }, [isOpen, adjustPosition]);

    const contextValue = useMemo(() => ({
        containerRef,
        triggerRef,
        settings,
        position,
        isOpen,
        cancelDelayedHide,
        hide: hideDropdown,
        show: showDropdown
    }), [hideDropdown, isOpen, position, settings, showDropdown]);

    return (
        <Context.Provider
            value={contextValue}
        >
            {children}
        </Context.Provider>
    );
}
